import * as React from 'react'
import { PropsWithChildren, useContext } from 'react'
import {
  type AccountAuthUser,
  getCurrentAuthUser,
  handleMagicLinkRedirectUrl,
  isMagicLinkRedirectUrl,
  observeAuthState,
  signOutAuth
} from '../async/account'
import { attempt } from '../async/utils'
import { trackSupabaseEvent } from '../async/supabase'
import { type AccountActionHistoryItem, type AccountRecord } from '../types/account-types'
import {
  getActionHistory,
  getFavorites,
  PartialUserActionHistory,
  toggleActionTaken,
  toggleFavorite
} from '../utilities/account'
import { trackSegmentEvent } from '../utilities/analytics'
import { handleAuthUser } from '../utilities/auth'
import { redirect } from '../utilities/routing'
import { UserActionHistoryData } from '../types/component-types'

// Magic-link OTP tokens are single-use. React 18 StrictMode double-invokes
// effects and Gatsby can remount AuthProvider, so the redirect handler could run
// more than once — the first verifyOtp consumes the token, the second fails with
// "token expired/invalid". This module-scoped promise ensures the exchange runs
// at most once per page load; all callers await the same in-flight result.
let magicLinkExchange: Promise<unknown> | undefined
const runMagicLinkExchangeOnce = () => {
  if (!magicLinkExchange) {
    magicLinkExchange = handleMagicLinkRedirectUrl()
  }
  return magicLinkExchange
}

export const AuthContext = React.createContext<{
  authUser?: AccountAuthUser | null
  authUserLoading?: boolean
  account?: AccountRecord
  accountLoading?: boolean
  isUpdatingUser?: boolean
  authRedirect?: string
  ignorePremiumRedirect?: boolean
  favorites?: string[] | null
  actionsTaken?: (AccountActionHistoryItem | PartialUserActionHistory)[] | null
  setAccount: (account: AccountRecord) => void
  setIsUpdatingUser: (loading: boolean) => void
  setAuthRedirect: (route?: string) => void
  setIgnorePremiumRedirect: (ignore: boolean) => void
  updateFavorite: (slug: string, favorited: boolean) => void
  updateActionTaken: (action: Queries.SanityAction, action_taken: boolean) => void
  logout: () => void
}>({
  authUser: null,
  setAccount: () => {},
  setIsUpdatingUser: () => {},
  setAuthRedirect: () => {},
  setIgnorePremiumRedirect: () => {},
  updateFavorite: async () => {},
  updateActionTaken: async () => {},
  logout: () => {}
})

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isUpdatingUser, setIsUpdatingUser] = React.useState(false)
  const [authRedirect, setAuthRedirect] = React.useState<string>()
  const [handledPotentialLogIn, setHandledPotentialLogIn] = React.useState<boolean>(false)
  const [ignorePremiumRedirect, setIgnorePremiumRedirect] = React.useState(false)
  const [authUser, setAuthUser] = React.useState(getCurrentAuthUser() || undefined)
  const [authUserLoading, setAuthUserLoading] = React.useState(true)
  const [account, setAccount] = React.useState<AccountRecord>()
  const [accountLoading, setAccountLoading] = React.useState<boolean>(true)
  const [favorites, setFavorites] = React.useState(getFavorites(account))
  const [actionsTaken, setActionsTaken] = React.useState<PartialUserActionHistory[]>(
    getActionHistory(account)
  )

  React.useEffect(() => {
    const attemptLoginIfApplicable = async () => {
      if (!authUser && isMagicLinkRedirectUrl()) {
        // if user is coming from a verification email, try and authenticate them.
        // Deduped so the single-use token is only exchanged once (see above).
        try {
          await runMagicLinkExchangeOnce()
        } catch {
          await redirect('/access-account')
        } finally {
          setHandledPotentialLogIn(true)
        }
      }

      setHandledPotentialLogIn(true)
    }

    attemptLoginIfApplicable()
  }, [])

  React.useEffect(() => {
    if (handledPotentialLogIn) {
      // when we auth gate a page, we often set a redirect to point the user
      // to when they become authenticated; this manages that process
      const unsubscribe = observeAuthState(async nextAuthUser => {
        if (nextAuthUser == null) {
          setAccount(undefined)
          setAccountLoading(false)
          setFavorites([])
          setActionsTaken([])
        }

        // supabase-js re-fires onAuthStateChange on tab focus/token refresh with
        // a freshly-built user object. Only update state when the identity truly
        // changed — otherwise the new object reference re-runs the account-load
        // effect (deps: [authUser]) and flashes the full-page loader on every
        // refocus. Compare by stable identity keys, not reference.
        setAuthUser(current => {
          const sameIdentity =
            (current?.authUserId ?? null) === (nextAuthUser?.authUserId ?? null) &&
            (current?.uid ?? null) === (nextAuthUser?.uid ?? null)
          return sameIdentity ? current : nextAuthUser || undefined
        })

        setAuthUserLoading(false)
        if (nextAuthUser && nextAuthUser.emailVerified && authRedirect) {
          redirect(authRedirect)
          setAuthRedirect(undefined)
        }
      })

      return () => unsubscribe()
    }
  }, [authRedirect, handledPotentialLogIn])

  const onLoadedAccount = (loadedAccount: AccountRecord) => {
    setAccount(loadedAccount)
    setAccountLoading(false)
  }

  const updateAccountSavedActions = React.useCallback(
    (slug: string, favorited: boolean) => {
      setAccount(currentAccount => {
        if (!currentAccount?.user) {
          return currentAccount
        }

        const currentSavedActions = currentAccount.user.saved_actions || []
        const nextSavedActions = favorited
          ? currentSavedActions.includes(slug)
            ? currentSavedActions
            : [...currentSavedActions, slug]
          : currentSavedActions.filter(savedSlug => savedSlug !== slug)

        return {
          ...currentAccount,
          user: {
            ...currentAccount.user,
            saved_actions: nextSavedActions
          }
        }
      })
    },
    []
  )

  React.useEffect(() => {
    let isActive = true

    const handle = async () => {
      if (authUser) {
        setAccountLoading(true)
      }

      // note: account data is loaded and refreshed by the active account seam
      // during this method
      await attempt(
        async () => {
          const loadedAccount = await handleAuthUser(authUser, loadedAccount => {
            if (!isActive) {
              return
            }

            onLoadedAccount(loadedAccount)
          })

          if (!isActive) {
            return
          }

          if (authUser && !loadedAccount) {
            console.warn('Authenticated user did not resolve to an account record', {
              authUserId: authUser.authUserId ?? null,
              email: authUser.email ?? null,
              uid: authUser.uid ?? null
            })
            setAccountLoading(false)
          }
        },
        err => {
          if (!isActive) {
            return
          }

          // if an error during auth occurs, log out the user
          alert(
            'An error occured while authenticating your login credentials: ' +
              (err || '') +
              'You will be logged out as a result; please try logging in again.'
          )
          logout()
        }
      )
    }
    handle()

    return () => {
      isActive = false
    }
  }, [authUser])

  const updateFavorite = React.useCallback(
    async (slug: string, favorited: boolean) => {
      setIsUpdatingUser(true)
      const nextFavorites = await toggleFavorite(slug, favorited, account)
      setFavorites(nextFavorites || [])
      if (nextFavorites) {
        updateAccountSavedActions(slug, favorited)
      }
      if (account?.id) {
        void trackSupabaseEvent({
          eventType: favorited ? 'action_favorited' : 'action_unfavorited',
          userId: account.id,
          actionSlug: slug,
          occurredAt: new Date().toISOString(),
          metadata: { source: 'actiontoolwebapp' }
        }).catch(error => {
          console.warn('Supabase track_event failed', error)
        })
      }
      setIsUpdatingUser(false)
    },
    [account, updateAccountSavedActions]
  )

  const updateActionTaken = React.useCallback(
    async (action: Queries.SanityAction | UserActionHistoryData, action_taken: boolean) => {
      setIsUpdatingUser(true)
      setActionsTaken(
        (await toggleActionTaken(action.slug.current, action_taken, account, {
          actionId: 'action_id' in action ? action.action_id : undefined
        })) || []
      )
      setIsUpdatingUser(false)
      trackSegmentEvent('Action Taken', {
        action_type: action.categories?.map(category => category.title),
        organization_name: action.organization?.title
      })
    },
    [account]
  )

  // update account favorites on login/out
  React.useEffect(() => {
    setFavorites(getFavorites(account))
    setActionsTaken(getActionHistory(account))
  }, [account])

  const logout = async () => {
    setIsUpdatingUser(true)
    await signOutAuth()
      .then(() => {
        setAccount(undefined)
        setAccountLoading(false)
        setAuthUser(undefined)
        setIsUpdatingUser(false)
        setActionsTaken([])
        setFavorites([])
        // Sign-out successful.
      })
      .catch(() => {
        // An error happened.
      })
    setIsUpdatingUser(false)
  }

  return (
    <AuthContext.Provider
      value={{
        account,
        authUser,
        authUserLoading,
        accountLoading,
        isUpdatingUser,
        authRedirect,
        favorites,
        ignorePremiumRedirect,
        actionsTaken,
        setAccount,
        setIsUpdatingUser,
        setAuthRedirect,
        setIgnorePremiumRedirect,
        updateFavorite,
        updateActionTaken,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const authState = () => useContext(AuthContext)
